/**
 * CostPatientsTab Component
 * 
 * Comprehensive view of ALL patients across ALL measures with:
 * - High-Cost filter (80% of cost cohort)
 * - Cost Rank column
 * - Risk Flags (Readmissions Watch badge)
 * - Search by MBI
 * - Filter by Measure
 * - Filter by Strategy (updated for CDM framework)
 * - Export to CSV
 * 
 * @see MIPS_Cost_Dashboard_Strategy_Redesign_Specification_v1.1
 */

import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  Download,
  Eye,
  Users,
  Filter,
  ArrowUpDown,
  AlertTriangle,
  TrendingUp,
} from 'lucide-react';
import { PatientDetailModal } from './PatientDetailModal';

// ============================================
// Types
// ============================================

interface Patient {
  mbi: string;
  gender: string;
  dob: string;
  dateOfDeath?: string | null;
  age: number;
  hccRisk: number;
  hccPercentile: number;
  totalCost: number;
  nationalAvg: number;
  excessCost: number;
  measures: string[];
  measureCount: number;
  hasReadmission: boolean;
  hasSNF: boolean;
  chronicConditionCount: number;
  strategies: { id: string; name: string; shortName: string; reason: string }[];
  // New fields from strategy redesign
  costRank?: number;
  isHighCost?: boolean;
  cdmTier?: number;
  riskFlags?: string[];
}

interface HighCostAnalysis {
  patientCount: number;
  percentOfPopulation: number;
  costThreshold: number;
  avgCostInCohort: number;
  totalCostInCohort: number;
}

interface CostPatientsTabProps {
  practiceId: string;
  npi?: string;
}

// ============================================
// Strategy Definitions (updated for CDM framework)
// ============================================

const STRATEGY_OPTIONS = [
  { id: 'all', name: 'All Strategies' },
  { id: 'cdm', name: 'Chronic Disease Management' },
  { id: 'pcm', name: 'PCM Enrollment' },
  { id: 'snf', name: 'SNF Optimization' },
  { id: 'ed', name: 'ED Diversion' },
  { id: 'medopt', name: 'Medication Optimization' },
  { id: 'bh', name: 'Behavioral Health' },
];

type SortField = 'costRank' | 'totalCost' | 'excessCost' | 'hccRisk' | 'age';
type SortDirection = 'asc' | 'desc';

// ============================================
// Helper Functions
// ============================================

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${Math.round(value / 1000)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  } catch {
    return '—';
  }
}

// ============================================
// Component
// ============================================

export function CostPatientsTab({ practiceId, npi }: CostPatientsTabProps) {
  // State
  const [patients, setPatients] = useState<Patient[]>([]);
  const [measureOptions, setMeasureOptions] = useState<{ id: string; name: string }[]>([]);
  const [highCostAnalysis, setHighCostAnalysis] = useState<HighCostAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [measureFilter, setMeasureFilter] = useState('all');
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [highCostFilterEnabled, setHighCostFilterEnabled] = useState(false);
  const [showAllPatients, setShowAllPatients] = useState(false);
  
  // Sorting
  const [sortField, setSortField] = useState<SortField>('costRank');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Modal
  const [selectedPatientMbi, setSelectedPatientMbi] = useState<string | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  // Load patients on mount
  useEffect(() => {
    loadPatients();
    loadHighCostAnalysis();
  }, [practiceId, npi]);

  const loadPatients = async () => {
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      
      const url = new URL(`${apiUrl}/api/practices/${practiceId}/cost/patients`);
      url.searchParams.set('limit', '10000'); // Get all patients
      if (npi) url.searchParams.set('npi', npi);
      
      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Sort by total cost descending to assign cost rank
        const sorted = [...data].sort((a, b) => (b.totalCost || 0) - (a.totalCost || 0));
        const withRanks = sorted.map((p, idx) => ({
          ...p,
          costRank: p.costRank || idx + 1,
        }));
        
        setPatients(withRanks);
        
        // Build measure options from patient data
        const measures = new Set<string>();
        data.forEach((p: Patient) => {
          p.measures?.forEach(m => measures.add(m));
        });
        
        const measureList = Array.from(measures)
          .sort()
          .map(m => ({
            id: m,
            name: getMeasureName(m),
          }));
        
        setMeasureOptions(measureList);
      }
    } catch (err) {
      console.error('Error loading patients:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadHighCostAnalysis = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      const url = new URL(`${apiUrl}/api/practices/${practiceId}/cost/portfolio`);
      if (npi) url.searchParams.set('npi', npi);
      
      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.highCostAnalysis) {
          setHighCostAnalysis(data.highCostAnalysis);
        }
      }
    } catch (err) {
      console.error('Error loading high-cost analysis:', err);
    }
  };

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'costRank' ? 'asc' : 'desc');
    }
  };

  // Filter and sort patients
  const filteredPatients = useMemo(() => {
    let result = patients;
    
    // MBI search
    if (searchTerm) {
      result = result.filter(p =>
        p.mbi.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Measure filter
    if (measureFilter !== 'all') {
      result = result.filter(p => p.measures?.includes(measureFilter));
    }
    
    // Strategy filter (map cdm to ccm for legacy data compatibility)
    if (strategyFilter !== 'all') {
      result = result.filter(p => {
        const strategies = Array.isArray(p.strategies) ? p.strategies : [];
        const hasStrategy = strategies.some(s => {
          if (strategyFilter === 'cdm') {
            // CDM includes both ccm and rpm patients
            return s.id === 'ccm' || s.id === 'rpm' || s.id === 'cdm';
          }
          return s.id === strategyFilter;
        });
        return hasStrategy;
      });
    }
    
    // High cost filter
    if (highCostFilterEnabled && highCostAnalysis) {
      result = result.filter(p => 
        p.isHighCost || 
        (p.costRank && p.costRank <= highCostAnalysis.patientCount)
      );
    }
    
    // Sort
    result = [...result].sort((a, b) => {
      let aVal: number, bVal: number;
      
      switch (sortField) {
        case 'costRank':
          aVal = a.costRank || 9999;
          bVal = b.costRank || 9999;
          break;
        case 'totalCost':
          aVal = a.totalCost || 0;
          bVal = b.totalCost || 0;
          break;
        case 'excessCost':
          aVal = a.excessCost || 0;
          bVal = b.excessCost || 0;
          break;
        case 'hccRisk':
          aVal = a.hccRisk || 0;
          bVal = b.hccRisk || 0;
          break;
        case 'age':
          aVal = a.age || 0;
          bVal = b.age || 0;
          break;
        default:
          aVal = a.costRank || 9999;
          bVal = b.costRank || 9999;
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return result;
  }, [patients, searchTerm, measureFilter, strategyFilter, highCostFilterEnabled, highCostAnalysis, sortField, sortDirection]);

  const displayedPatients = showAllPatients 
    ? filteredPatients 
    : filteredPatients.slice(0, 25);

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'Cost Rank', 'MBI', 'Gender', 'DOB', 'Age', 'Deceased Date', 'HCC Risk', 'HCC Percentile',
      'Total Cost', 'National Avg', 'Opportunity', 'High Cost', 'Measure Count',
      'Measures', 'Risk Flags', 'CDM Tier', 'Recommended Strategies'
    ];
    
    const rows = filteredPatients.map(patient => [
      patient.costRank || '',
      patient.mbi,
      patient.gender,
      patient.dob,
      patient.age,
      patient.dateOfDeath || '',
      patient.hccRisk?.toFixed(2) || '',
      patient.hccPercentile || '',
      patient.totalCost?.toFixed(2) || '',
      patient.nationalAvg?.toFixed(2) || '',
      Math.max(0, patient.excessCost)?.toFixed(2) || '',
      patient.isHighCost ? 'Yes' : 'No',
      patient.measureCount || patient.measures?.length || 0,
      patient.measures?.join(';') || '',
      patient.hasReadmission ? 'Readmission' : '',
      patient.cdmTier || '',
      (Array.isArray(patient.strategies) ? patient.strategies : []).map(s => s.shortName).join(';') || ''
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'patients_with_strategies.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Sortable column header
  const SortableHeader = ({ field, label }: { field: SortField; label: string }) => (
    <th 
      className="p-3 font-medium cursor-pointer hover:bg-muted/80 transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center justify-end gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${sortField === field ? 'text-primary' : 'text-muted-foreground'}`} />
      </div>
    </th>
  );

  // Loading state - no animation here, appears instantly like Summary page
  if (isLoading) {
    return (
      <div className="space-y-6 min-h-[500px]">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-48" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 min-h-[500px] animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">All Patients</h2>
          <p className="text-sm text-muted-foreground">
            {filteredPatients.length} patients • Sorted by {sortField === 'costRank' ? 'cost rank' : sortField}
          </p>
        </div>
        
        {highCostAnalysis && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 px-3 py-2 rounded-lg">
            <TrendingUp className="h-4 w-4" />
            <span>
              <strong>{highCostAnalysis.patientCount}</strong> patients 
              ({Math.round(highCostAnalysis.percentOfPopulation)}%) drive 80% of cost
            </span>
          </div>
        )}
      </div>

      {/* Search & Filter Bar */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* MBI Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by MBI..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Measure Filter */}
        <Select value={measureFilter} onValueChange={setMeasureFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Measures" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Measures</SelectItem>
            {measureOptions.map(m => (
              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Strategy Filter */}
        <Select value={strategyFilter} onValueChange={setStrategyFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Strategies" />
          </SelectTrigger>
          <SelectContent>
            {STRATEGY_OPTIONS.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* High Cost Filter */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="highCostFilter"
            checked={highCostFilterEnabled}
            onCheckedChange={(checked) => setHighCostFilterEnabled(checked === true)}
          />
          <label htmlFor="highCostFilter" className="text-sm cursor-pointer flex items-center gap-1">
            <Filter className="h-3 w-3" />
            High-Cost Only
          </label>
        </div>

        {/* Export Button */}
        <Button variant="outline" onClick={exportToCSV} className="ml-auto">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Patient Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <SortableHeader field="costRank" label="Rank" />
              <th className="text-left p-3 font-medium">MBI</th>
              <th className="text-center p-3 font-medium">Gender</th>
              <SortableHeader field="age" label="Age" />
              <th className="text-center p-3 font-medium">DOB</th>
              <th className="text-center p-3 font-medium">Deceased</th>
              <SortableHeader field="hccRisk" label="HCC Risk" />
              <SortableHeader field="totalCost" label="Total Cost" />
              <SortableHeader field="excessCost" label="Opportunity" />
              <th className="text-center p-3 font-medium"># Measures</th>
              <th className="text-center p-3 font-medium">Risk Flags</th>
              <th className="text-left p-3 font-medium">Strategies</th>
              <th className="text-center p-3 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {displayedPatients.length > 0 ? (
              displayedPatients.map((patient, idx) => {
                const isHighCost = patient.isHighCost || 
                  (patient.costRank && highCostAnalysis && patient.costRank <= highCostAnalysis.patientCount);
                
                return (
                  <tr key={idx} className="border-t hover:bg-muted/30">
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className="font-mono text-muted-foreground">
                          #{patient.costRank || '—'}
                        </span>
                        {isHighCost && (
                          <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-xs px-1">
                            HC
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3 font-mono">{patient.mbi}</td>
                    <td className="p-3 text-center">{patient.gender}</td>
                    <td className="p-3 text-right">{patient.age}</td>
                    <td className="p-3 text-center text-muted-foreground text-xs">
                      {formatDate(patient.dob)}
                    </td>
                    <td className="p-3 text-center text-muted-foreground text-xs">
                      {formatDate(patient.dateOfDeath)}
                    </td>
                    <td className="p-3 text-right font-mono">
                      {patient.hccRisk?.toFixed(2) || '—'}
                    </td>
                    <td className="p-3 text-right font-mono">
                      {formatCurrency(patient.totalCost)}
                    </td>
                    <td className="p-3 text-right font-semibold text-amber-600">
                      {patient.excessCost > 0 ? formatCurrency(patient.excessCost) : '—'}
                    </td>
                    <td className="p-3 text-center">
                      <Badge variant="outline">{patient.measureCount || patient.measures?.length || 0}</Badge>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {patient.hasReadmission && (
                          <Badge className="bg-red-100 text-red-700 border border-red-300 text-xs px-1.5" title="Readmissions Watch">
                            <AlertTriangle className="h-3 w-3 mr-0.5" />
                            R
                          </Badge>
                        )}
                        {patient.hasSNF && (
                          <Badge className="bg-blue-100 text-blue-700 border border-blue-300 text-xs px-1" title="SNF">
                            S
                          </Badge>
                        )}
                        {patient.cdmTier === 2 && (
                          <Badge className="bg-purple-100 text-purple-700 border border-purple-300 text-xs px-1" title="CDM Tier 2">
                            T2
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(patient.strategies) ? patient.strategies : []).slice(0, 3).map(s => (
                          <Badge 
                            key={s.id} 
                            className="bg-blue-100 text-blue-700 border-blue-300 text-xs"
                          >
                            {s.shortName || s.name || s.id?.toUpperCase()}
                          </Badge>
                        ))}
                        {Array.isArray(patient.strategies) && patient.strategies.length > 3 && (
                          <span className="text-xs text-muted-foreground">
                            +{patient.strategies.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedPatientMbi(patient.mbi)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={13} className="p-8 text-center text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-20" />
                  No patients found matching your filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {displayedPatients.length} of {filteredPatients.length} patients
        </p>
        {filteredPatients.length > 25 && (
          <Button
            variant="outline"
            onClick={() => setShowAllPatients(!showAllPatients)}
          >
            {showAllPatients ? 'Show Less' : 'Show All'}
          </Button>
        )}
      </div>

      {/* Patient Detail Modal */}
      <PatientDetailModal
        isOpen={!!selectedPatientMbi}
        onClose={() => setSelectedPatientMbi(null)}
        patientMbi={selectedPatientMbi}
        practiceId={practiceId}
        npi={npi}
      />
    </div>
  );
}

// ============================================
// Helper: Measure name lookup
// ============================================

function getMeasureName(measureId: string): string {
  const names: Record<string, string> = {
    'TPCC_1': 'Total Per Capita Cost',
    'MSPB_1': 'MSPB Clinician',
    'COST_D_1': 'Diabetes',
    'COST_HF_1': 'Heart Failure',
    'COST_ACOPD_1': 'Asthma/COPD',
    'COST_DEP_1': 'Depression',
    'COST_LBP_1': 'Low Back Pain',
    'COST_RA_1': 'Rheumatoid Arthritis',
    'COST_CKD_1': 'Chronic Kidney Disease',
    'COST_ESRD_1': 'End Stage Renal Disease',
    'COST_S_1': 'Sepsis',
    'COST_STEMI_1': 'STEMI with PCI',
    'COST_COPDE_1': 'IP COPD Exacerbation',
    'COST_EDV_1': 'Emergency Medicine',
    'COST_PRC_1': 'Psychoses',
  };
  return names[measureId] || measureId;
}

export default CostPatientsTab;
